import { AABB, ObjectId, View } from "@novorender/api";
import { BoundingSphere, HierarcicalObjectReference, ObjectData, ObjectDB } from "@novorender/data-js-api";
import { quat, ReadonlyVec3, vec3 } from "gl-matrix";
import pMap from "p-map";

// Type-only: `dataAccess`' barrel reaches back into this module through
// `objectDbDataAccess`, so a value import here is a genuine cycle.
import type { ObjectDataAccess } from "dataAccess";
import { flip } from "features/render/utils";
import { NodeType } from "types/misc";

import { AppObjectDB } from "./appObjectDb";
import { mergeBoundingSpheres } from "./math";
import { getFilePathFromObjectPath, getTopmostFilePathFromObjectPath } from "./objectPath";
import { batchedPropertySearch, getObjectData } from "./search";
import { IdList } from "./selectionSet";
export {
    getAncestorSearchDepth,
    getFilePathFromObjectPath,
    getNodeClass,
    getParentPaths,
    getTopmostFilePathFromObjectPath,
    isContainerFileNode,
    isLeafNode,
    NODE_CLASS_PROPERTY,
    NodeClass,
} from "./objectPath";

/** Property groups generated by the IFC parser that we hide for now (BM-11063). */
const IFC_HIDDEN_GROUPS = new Set(["Quantities", "Location"]);

export function decodeObjPathName(str: string) {
    try {
        return decodeURIComponent(str);
    } catch {
        return str.replaceAll("%2f", "/").replaceAll("%20", " ");
    }
}

export function getParentPath(path: string): string {
    const lastSlashIdx = path.lastIndexOf("/");
    return lastSlashIdx > 0 ? path.slice(0, lastSlashIdx) : "";
}

export function extractObjectIds<T extends { id: number } = HierarcicalObjectReference>(
    objects: { id: number }[],
): T["id"][] {
    return objects.map((obj) => obj.id);
}

export function getObjectNameFromPath(path: string): string {
    const arr = path.split("/");

    return decodeObjPathName(arr.at(-1) ?? path);
}

export async function getObjectMetadataRotation(
    view: View,
    db: ObjectDB,
    objectId: ObjectId,
): Promise<quat | undefined> {
    const metadata = await (view.data ? view.data.getObjectMetaData(objectId) : db.getObjectMetdata(objectId));

    const filePath = getFilePathFromObjectPath(metadata.path);
    if (!filePath) {
        return;
    }

    const [descendantName] = metadata.path.substring(filePath.length + 1).split("/", 1);
    if (!descendantName) {
        return;
    }

    const descendantPath = `${filePath}/${descendantName}`;

    const objects = db.search(
        {
            descentDepth: 0,
            parentPath: descendantPath,
            full: true,
        },
        undefined,
    );

    for await (const object of objects) {
        const fileMetadata = await object.loadMetaData();
        const rotationProp = fileMetadata.properties.find((p) => p[0] === "Novorender/Rotation")?.[1];
        if (rotationProp) {
            return JSON.parse(rotationProp);
        }
    }
}

/**
 * Determines whether an object node represents a PDF/drawing document.
 *
 * PDFs normally carry a ".pdf" extension, but some (notably ones created via model
 * federation) are stored without a (proper) extension. Those still need to be
 * filtered out of the model tree, so when the path doesn't resolve to a recognized
 * model-file extension we fall back to the "Procore/DocumentSource" metadata property
 * (which requires loading the object metadata) and treat a value of "drawings" as a PDF.
 *
 * To avoid unnecessary metadata loads, any node whose path already resolves to a
 * recognized, non-PDF model-file extension is treated as a non-PDF without
 * inspecting its metadata.
 *
 * @param options.knownPdfObjectIds The scene's document ids from Procore's discovery
 * (`utils/pdfObjectIds`), which admits a search hit only when its path satisfies the same
 * rule as below. Given one, it answers on its own and neither path nor metadata is read: a
 * hit is a document, a miss is not. Pass it only once discovery has finished — the callers'
 * `readyPdfObjectIds` is `undefined` until then — since a partial set would pass a sheet off
 * as a model.
 * @see isPdfOrDrawingObjectId in `dataAccess/utils` for the variant that holds only an id.
 */
export async function isPdfOrDrawingObject(
    object: HierarcicalObjectReference,
    options?: { knownPdfObjectIds?: Pick<Set<ObjectId>, "has"> },
): Promise<boolean> {
    if (options?.knownPdfObjectIds) {
        return options.knownPdfObjectIds.has(object.id);
    }

    const filePath = getFilePathFromObjectPath(object.path);
    if (filePath) {
        return filePath.toLowerCase().endsWith(".pdf");
    }

    try {
        const metadata = await object.loadMetaData();
        return hasDrawingsDocumentSource(metadata.properties);
    } catch {
        return false;
    }
}

/** Server-generated Procore document source identifier marking the object as a drawing. */
export function hasDrawingsDocumentSource(properties: readonly (readonly [key: string, value: string])[]): boolean {
    const documentSource = properties.find(([key]) => key === "Procore/DocumentSource")?.[1];
    return documentSource?.toLowerCase() === "drawings";
}

function getBoxBoundsW(box: AABB, normal: ReadonlyVec3): { min: number; max: number } | undefined {
    const corners = [
        vec3.fromValues(box.min[0], box.min[1], box.min[2]),
        vec3.fromValues(box.min[0], box.min[1], box.max[2]),
        vec3.fromValues(box.min[0], box.max[1], box.min[2]),
        vec3.fromValues(box.min[0], box.max[1], box.max[2]),
        vec3.fromValues(box.max[0], box.min[1], box.min[2]),
        vec3.fromValues(box.max[0], box.min[1], box.max[2]),
        vec3.fromValues(box.max[0], box.max[1], box.min[2]),
        vec3.fromValues(box.max[0], box.max[1], box.max[2]),
    ];

    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    for (const corner of corners) {
        const w = vec3.dot(corner, normal);
        min = Math.min(min, w);
        max = Math.max(max, w);
    }

    return Number.isFinite(min) && Number.isFinite(max) ? { min, max } : undefined;
}

export async function getObjectBoundsW(
    view: View,
    db: ObjectDB | undefined,
    objectId: ObjectId,
    normal: ReadonlyVec3,
): Promise<{ min: number; max: number } | undefined> {
    const metadata = await (view.data ? view.data.getObjectMetaData(objectId) : db?.getObjectMetdata(objectId));
    const box = metadata?.bounds?.box;
    if (!box) {
        return;
    }

    return getBoxBoundsW(box, normal);
}

export async function getBigEnoughObjectBoundsW(
    view: View,
    db: ObjectDB | undefined,
    objectId: ObjectId,
    normal: ReadonlyVec3,
): Promise<{ min: number; max: number } | undefined> {
    const metadata = await (view.data ? view.data.getObjectMetaData(objectId) : db?.getObjectMetdata(objectId));
    if (!metadata?.bounds?.box) {
        return;
    }

    const objectRange = getBoxBoundsW(metadata.bounds.box, normal);
    const MIN_RANGE_SIZE = 5;

    if ((objectRange && objectRange.max - objectRange.min >= MIN_RANGE_SIZE) || !db) {
        return objectRange;
    }

    // Traverse objects up the hierarchy until we find a range that is big enough
    try {
        const iterator = db.search(
            {
                parentPath: metadata.path,
                descentDepth: -(metadata.path.split("/").length + 1),
            },
            undefined,
        );

        const parents = await Array.fromAsync(iterator);
        parents.sort((a, b) => b.path.length - a.path.length);

        for (const object of parents) {
            if (!object.bounds?.box) {
                continue;
            }
            const range = getBoxBoundsW(object.bounds.box, normal);
            if (range && range.max - range.min >= MIN_RANGE_SIZE) {
                return range;
            }
        }
    } catch (error) {
        console.error(error);
    }
}

export function getFileNameFromPath(path: string): string | null {
    const filePath = getFilePathFromObjectPath(path);

    if (!filePath) {
        return null;
    }

    const fileName = filePath.includes("/") ? (filePath.split("/").at(-1) ?? null) : filePath;
    return fileName ? decodeObjPathName(fileName) : null;
}

/**
 * Like {@link getFileNameFromPath}, but based on the outermost/topmost file node in
 * the path (see {@link getTopmostFilePathFromObjectPath}) rather than the innermost
 * one. Strips any preceding path fragments (e.g. a federation root node's name), so
 * this always returns a bare file name rather than a partial path.
 */
export function getTopmostFileNameFromPath(path: string): string | null {
    const filePath = getTopmostFilePathFromObjectPath(path);

    if (!filePath) {
        return null;
    }

    const fileName = filePath.includes("/") ? (filePath.split("/").at(-1) ?? null) : filePath;
    return fileName ? decodeObjPathName(fileName) : null;
}

/**
 * Cap for resolving a selection's combined bounds via {@link objIdsToTotalBoundingSphere}
 * (ObjectDB metadata search, batched over every id). Above this, callers skip the lookup:
 * axis-sphere falls back to view-center focus; SpaceMouse uses the scene volume instead.
 */
export const MAX_SELECTION_BOUNDS_OBJECT_IDS = 5000;

export async function objIdsToTotalBoundingSphere({
    ids,
    abortSignal,
    db,
    flip,
    view,
}: {
    ids: IdList;
    abortSignal: AbortSignal;
    db: AppObjectDB;
    flip?: boolean;
    view: View;
}) {
    // Normalize to a plain array (ids may be a selection set's Uint32Array).
    const idArr = Array.isArray(ids) ? (ids as number[]) : Array.from(ids);
    // If there's only one id - use getObjectData instead, because it may hit cache
    const nodes =
        navigator.onLine && idArr.length > 1
            ? await batchedPropertySearch({
                  db,
                  property: "id",
                  value: idArr.map(String),
                  abortSignal,
              })
            : ((await Promise.all(idArr.slice(-50).map((id) => getObjectData({ db, id, view })))).filter(
                  (obj): obj is ObjectData => obj !== undefined,
              ) as HierarcicalObjectReference[]);

    return getTotalBoundingSphere(nodes, { flip });
}

export function getTotalBoundingSphere(
    nodes: HierarcicalObjectReference[],
    options?: { flip?: boolean },
): BoundingSphere | undefined {
    const spheres: BoundingSphere[] = [];

    const shouldFlip = options?.flip === true;
    for (const node of nodes) {
        const sphere = node.bounds?.sphere;

        if (sphere) {
            spheres.push(
                shouldFlip
                    ? {
                          ...sphere,
                          center: flip(sphere.center),
                      }
                    : sphere,
            );
        }
    }

    return mergeBoundingSpheres(spheres);
}

/**
 * Resolves a combined bounding sphere for a set of top-level selections.
 *
 * For each entry, we first try `getObjectData` (O(1) cache hit for already-loaded
 * objects). Folder nodes often have a pre-computed `bounds.sphere`, so this is
 * typically free. When a node has no sphere we do a single shallow db.search one
 * level down (descentDepth: 0 returns immediate children) and accumulate their
 * spheres, repeating up to `maxFallbackDepth` times before giving up on that entry.
 *
 * Returns undefined when none of the entries resolve to a sphere, letting the
 * caller fall back to batchedPropertySearch.
 */
export async function resolveTopLevelBoundingSphere({
    topLevelSelections,
    dataAccess,
    abortSignal,
    flipCoords,
}: {
    topLevelSelections: Record<number, { path: string }>;
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    flipCoords?: boolean;
}): Promise<BoundingSphere | undefined> {
    const ids = Object.keys(topLevelSelections);
    if (ids.length === 0) {
        return undefined;
    }

    const MAX_FALLBACK_DEPTH = 3;
    const collected: HierarcicalObjectReference[] = [];

    const searchQueue: HierarcicalObjectReference[] = [];

    await pMap(
        ids,
        async (idStr) => {
            if (abortSignal.aborted) {
                return;
            }
            const id = Number(idStr);
            const data = await dataAccess.getObject(id, abortSignal);
            if (data?.bounds?.sphere) {
                collected.push(data);
                return;
            }
            // No sphere on the node itself — queue the path for a shallow search.
            if (data && data.type !== NodeType.Leaf) {
                searchQueue.push(data);
            }
        },
        { concurrency: 10 },
    );

    if (abortSignal.aborted) {
        return undefined;
    }

    // For nodes without a direct sphere, walk one level at a time up to
    // MAX_FALLBACK_DEPTH until we find children with spheres.
    let currentNodes = searchQueue;
    for (let depth = 0; depth < MAX_FALLBACK_DEPTH && currentNodes.length > 0; depth++) {
        if (abortSignal.aborted || currentNodes.length === 0) {
            return undefined;
        }

        const nextNodes: HierarcicalObjectReference[] = [];
        await pMap(
            currentNodes,
            async (parent) => {
                if (abortSignal.aborted) {
                    return;
                }
                const iter = dataAccess.getChildren(parent, abortSignal);
                for await (const child of iter) {
                    if (abortSignal.aborted) {
                        return;
                    }
                    if (child.bounds?.sphere) {
                        collected.push(child);
                    } else if (child.type !== NodeType.Leaf) {
                        nextNodes.push(child);
                    }
                }
            },
            { concurrency: 10 },
        );

        currentNodes = nextNodes;
    }

    return getTotalBoundingSphere(collected, { flip: flipCoords });
}

export function getGuids(refs: HierarcicalObjectReference[]): Promise<string[]> {
    return Promise.all(refs.map((ref) => getGuid(ref))).then((guids) => guids.filter((guid) => guid !== ""));
}

export function getGuid(ref: HierarcicalObjectReference): Promise<string> {
    return ref.loadMetaData().then((obj) => {
        const guid = obj.properties.find((prop) => prop[0] === "GUID");
        return guid ? guid[1] : "";
    });
}

export function getPropertyDisplayName(property: string): string {
    let decoded: string | undefined = undefined;

    try {
        decoded = decodeURIComponent(property);
    } catch {
        console.warn(`Failed to decode property "${property}".`);
    }

    const display = decoded ?? property;

    return display[0]?.toUpperCase() + display.slice(1);
}

export function isUrl(str: string): boolean {
    return str.startsWith("http");
}

export function isTerrainObjectId(objectId: ObjectId) {
    return objectId <= View.terrainMaxId;
}

/**
 * Should the property be hidden in Properties, Property Tree, and other places?
 * NOTE: Not all internal properties are hidden.
 */
export function shouldHideProperty(property: string): boolean {
    const lc = property.toLowerCase();
    return lc === "procore" || lc.startsWith("procore/");
}

/** Whether the object belongs to an IFC file, based on its path. */
export function isIfcObjectPath(path: string): boolean {
    const filePath = getFilePathFromObjectPath(path);
    return filePath != null && /\.ifc$/i.test(filePath);
}

/**
 * Adjusts a property key for IFC objects (BM-11063):
 * - returns `null` for properties in hidden groups (see {@link IFC_HIDDEN_GROUPS})
 *
 * Properties without a group and properties in other groups are returned unchanged.
 */
export function transformIfcPropertyKey(property: string): string | null {
    const slashIdx = property.indexOf("/");
    if (slashIdx === -1) {
        return property;
    }

    const group = decodeObjPathName(property.slice(0, slashIdx));

    if (IFC_HIDDEN_GROUPS.has(group)) {
        return null;
    }

    return property;
}

/**
 * Filters an object's raw properties for display: drops empty and internal
 * (Procore/*) properties, and—only in the Procore Design (`isProcoreDesign`)—applies
 * IFC-specific group adjustments for objects in IFC files
 * (see {@link transformIfcPropertyKey}).
 */
export function getDisplayProperties(
    object: Pick<ObjectData, "path" | "properties">,
    isProcoreDesign = false,
): [string, string][] {
    const isIfc = isProcoreDesign && isIfcObjectPath(object.path);
    const result: [string, string][] = [];

    for (const [property, value] of object.properties) {
        if (!value || shouldHideProperty(property)) {
            continue;
        }

        const key = isIfc ? transformIfcPropertyKey(property) : property;
        if (key !== null) {
            result.push([key, value]);
        }
    }

    return result;
}
